import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import DashboardLayout from './components/layout/DashboardLayout';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import CustomersList from './pages/customers/CustomersList';
import CustomerDetail from './pages/customers/CustomerDetail';
import EmergencyRequestsList from './pages/EmergencyRequestsList';
import AppointmentsList from './pages/appointments/AppointmentsList';
import AppointmentDetail from './pages/appointments/AppointmentDetail';
import AssessmentsList from './pages/assessments/AssessmentsList';
import AssessmentDetail from './pages/assessments/AssessmentDetail';
import UsersList from './pages/users/UsersList';
import Settings from './pages/Settings';
import NotFound from './pages/NotFound';
import DiagnosticReport from './pages/DiagnosticReport';

// Blog management pages
import BlogPostsList from './pages/blog/BlogPostsList';
import BlogPostForm from './pages/blog/BlogPostForm';
import BlogPostPreview from './pages/blog/BlogPostPreview';

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return <div>Loading...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

// Role-protected route component
const RoleProtectedRoute = ({ children, allowedRoles }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return <div>Loading...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  if (!allowedRoles.includes(user?.role)) {
    return <Navigate to="/" />;
  }
  
  return children;
};

function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      
      {/* Protected routes inside the dashboard layout */}
      <Route path="/" element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Dashboard />} />
        <Route path="customers" element={
          <RoleProtectedRoute allowedRoles={['admin', 'manager', 'claims_manager']}>
            <CustomersList />
          </RoleProtectedRoute>
        } />
        <Route path="customers/new" element={
          <RoleProtectedRoute allowedRoles={['admin', 'manager']}>
            <CustomerDetail isEditing={true} isNew={true} />
          </RoleProtectedRoute>
        } />
        <Route path="customers/:id" element={
          <RoleProtectedRoute allowedRoles={['admin', 'manager', 'claims_manager']}>
            <CustomerDetail />
          </RoleProtectedRoute>
        } />
        <Route path="customers/:id/edit" element={
          <RoleProtectedRoute allowedRoles={['admin', 'manager']}>
            <CustomerDetail isEditing={true} />
          </RoleProtectedRoute>
        } />
        <Route path="emergency-requests" element={
          <RoleProtectedRoute allowedRoles={['admin', 'manager', 'claims_manager']}>
            <EmergencyRequestsList />
          </RoleProtectedRoute>
        } />
        <Route path="appointments" element={
          <RoleProtectedRoute allowedRoles={['admin', 'manager', 'claims_manager']}>
            <AppointmentsList />
          </RoleProtectedRoute>
        } />
        <Route path="appointments/new" element={
          <RoleProtectedRoute allowedRoles={['admin', 'manager']}>
            <AppointmentDetail isEditing={true} isNew={true} />
          </RoleProtectedRoute>
        } />
        <Route path="appointments/:id" element={
          <RoleProtectedRoute allowedRoles={['admin', 'manager', 'claims_manager']}>
            <AppointmentDetail />
          </RoleProtectedRoute>
        } />
        <Route path="appointments/:id/edit" element={
          <RoleProtectedRoute allowedRoles={['admin', 'manager']}>
            <AppointmentDetail isEditing={true} />
          </RoleProtectedRoute>
        } />
        <Route path="appointments/:id/reschedule" element={
          <RoleProtectedRoute allowedRoles={['admin', 'manager']}>
            <AppointmentDetail isEditing={true} isRescheduling={true} />
          </RoleProtectedRoute>
        } />
        <Route path="assessments" element={
          <RoleProtectedRoute allowedRoles={['admin', 'manager', 'claims_manager']}>
            <AssessmentsList />
          </RoleProtectedRoute>
        } />
        <Route path="assessments/:id" element={
          <RoleProtectedRoute allowedRoles={['admin', 'manager', 'claims_manager']}>
            <AssessmentDetail />
          </RoleProtectedRoute>
        } />
        <Route path="users" element={
          <RoleProtectedRoute allowedRoles={['admin']}>
            <UsersList />
          </RoleProtectedRoute>
        } />
        <Route path="settings" element={
          <RoleProtectedRoute allowedRoles={['admin', 'manager']}>
            <Settings />
          </RoleProtectedRoute>
        } />
        <Route path="diagnostics" element={
          <RoleProtectedRoute allowedRoles={['admin']}>
            <DiagnosticReport />
          </RoleProtectedRoute>
        } />
        
        {/* Blog management routes */}
        <Route path="blog" element={
          <RoleProtectedRoute allowedRoles={['admin', 'manager', 'blog_manager']}>
            <BlogPostsList />
          </RoleProtectedRoute>
        } />
        <Route path="blog/new" element={
          <RoleProtectedRoute allowedRoles={['admin', 'manager', 'blog_manager']}>
            <BlogPostForm />
          </RoleProtectedRoute>
        } />
        <Route path="blog/edit/:id" element={
          <RoleProtectedRoute allowedRoles={['admin', 'manager', 'blog_manager']}>
            <BlogPostForm />
          </RoleProtectedRoute>
        } />
        <Route path="blog/preview/:id" element={
          <RoleProtectedRoute allowedRoles={['admin', 'manager', 'blog_manager']}>
            <BlogPostPreview />
          </RoleProtectedRoute>
        } />
      </Route>
      
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default App;